import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import BackButton from 'src/components/BackButton';
import Button from 'src/components/Button';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import Autocomplete from 'src/components/Form/Autocomplete';
// import DatePicker from 'src/components/Form/DatePicker';
import DatePicker from 'src/components/Form/DatePicker';
import TextField from 'src/components/Form/TextField';
import { IEditParams } from 'src/interfaces/IEditParams';
import { IBenefit, ICategory, IPartner, IPerson } from 'src/interfaces/models';
import { IPartnerService } from 'src/interfaces/models/IPartner';
import {
  IPersonBonusVoucher,
  IPersonSubscription,
} from 'src/interfaces/models/IPerson';
import PrivateContext from 'src/routes/Private/PrivateContext';
import api from 'src/services/api';
import { handleApiResponseErrors, showFormErrors } from 'src/utils/errors';
import { formatDate } from 'src/utils/helpers';
import masks from 'src/utils/masks';
import yupValidate from 'src/utils/yupValidate';
import {
  PersonBonusAdminStoreSchema,
  PersonBonusAdminUpdateSchema,
} from 'src/validators/PersonBonus/save.schema';

import { Container } from './styles';

const Save: FC = () => {
  const { startLayoutLoading, stopLayoutLoading, layoutLoading } = useContext(
    PrivateContext,
  );
  const formRef = useRef<FormHandles>(null);
  const confirmDialogRef = useRef<ConfirmDialogRef>(null);
  const [activeBonus, setActiveBonus] = useState<IPersonBonusVoucher | null>(
    null,
  );
  const [persons, setPersons] = useState<IPerson[]>([]);
  const [personSubscriptions, setPersonSubscriptions] = useState<
    IPersonSubscription[]
  >([]);
  const [benefits, setBenefits] = useState<IBenefit[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [partnerServices, setPartnerServices] = useState<IPartnerService[]>([]);
  const [loading, setLoading] = useState(false);
  const [disableBenefit, setDisableBenefit] = useState(true);
  const [status, setStatus] = useState('');
  const history = useHistory();
  const params = useParams<IEditParams>();

  const yupSchema = params.id
    ? PersonBonusAdminUpdateSchema
    : PersonBonusAdminStoreSchema;

  useEffect(() => {
    const loadData = async () => {
      try {
        startLayoutLoading();

        const getPersons = api.get<IPerson[]>('/admin/persons');
        const getBenefits = api.get<IBenefit[]>('/admin/benefits', {
          params: {
            type: 'bonus',
          },
        });

        const [personsResp, benefitsResp] = await Promise.all([
          getPersons,
          getBenefits,
        ]);
        setPersons(personsResp.data);
        setBenefits(benefitsResp.data);

        if (params.id) {
          const response = await api.get<IPersonBonusVoucher>(
            `admin/persons-bonuses/${params.id}`,
          );
          const bonus = response.data;
          setActiveBonus(bonus);

          setStatus(bonus.status);
          formRef.current?.setData({
            ...bonus,
            person_id: bonus.personSubscription?.person_id,
          });
        }
      } catch (error) {
        handleApiResponseErrors(error.response, 'Erro ao buscar dados.');
      } finally {
        stopLayoutLoading();
      }
    };

    loadData();
  }, [params.id, startLayoutLoading, stopLayoutLoading]);

  const handleChangePerson = useCallback(async () => {
    try {
      setLoading(true);

      const personId = formRef.current?.getFieldValue('person_id');

      if (personId) {
        const personSubsResp = await api.get<IPersonSubscription[]>(
          '/admin/persons-subscriptions',
          {
            params: {
              person_id: personId,
              expired: false,
              allow_bonus: true,
              statuses: ['pending', 'active'],
            },
          },
        );
        setPersonSubscriptions(personSubsResp.data);

        if (personSubsResp.data.length === 0) {
          formRef.current?.setFieldValue('person_subscription_id', null);
        } else if (activeBonus?.person_subscription_id) {
          setTimeout(() =>
            formRef.current?.setFieldValue(
              'person_subscription_id',
              activeBonus?.person_subscription_id,
            ),
          );
        }

        // if (!activeBonus) {
        //   const selectedPerson = persons.find(
        //     (person) => person.id == personId,
        //   );
        //   formRef.current?.setFieldValue('name', selectedPerson?.name);
        //   formRef.current?.setFieldValue('document', selectedPerson?.document);
        //   formRef.current?.setFieldValue(
        //     'birth_date',
        //     selectedPerson?.birth_date,
        //   );
        // }
      } else {
        throw new Error('Erro ao buscar assinaturas .');
      }
    } catch (error) {
      setPersonSubscriptions([]);
      formRef.current?.setFieldValue('person_subscription_id', null);
      formRef.current?.setFieldValue('name', '');
      formRef.current?.setFieldValue('document', '');
      // formRef.current?.setFieldValue('birth_date', '');
    } finally {
      setLoading(false);
    }
  }, [activeBonus]);

  const handleChangePersonSubscription = useCallback(async () => {
    const personSubId = formRef.current?.getFieldValue(
      'person_subscription_id',
    );

    if (personSubId) {
      setDisableBenefit(false);
    } else {
      setDisableBenefit(true);
      formRef.current?.setFieldValue('benefit_id', null);
    }
  }, []);

  const handleBenefitChange = useCallback(async () => {
    try {
      setLoading(true);

      const benefitId = formRef.current?.getFieldValue('benefit_id');

      if (!benefitId) {
        setCategories([]);
        formRef.current?.setFieldValue('category_id', null);
        return;
      }

      const response = await api.get<ICategory[]>(`/admin/categories`, {
        params: { benefit_id: benefitId },
      });
      setCategories(response.data);

      if (activeBonus?.category_id) {
        setTimeout(() =>
          formRef.current?.setFieldValue(
            'category_id',
            activeBonus?.category_id,
          ),
        );
      }
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao buscar categorias.');
    } finally {
      setLoading(false);
    }
  }, [activeBonus]);

  const handleCategoryChange = useCallback(async () => {
    try {
      setLoading(true);

      const categoryId = formRef.current?.getFieldValue('category_id');

      if (!categoryId) {
        setPartners([]);
        formRef.current?.setFieldValue('partner_id', null);
        return;
      }

      const response = await api.get<IPartner[]>(`/admin/partners`, {
        params: { category_id: categoryId },
      });
      setPartners(response.data);

      if (activeBonus?.partner_id) {
        setTimeout(() =>
          formRef.current?.setFieldValue('partner_id', activeBonus?.partner_id),
        );
      }
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao buscar parceiras.');
    } finally {
      setLoading(false);
    }
  }, [activeBonus]);

  const handlePartnerChange = useCallback(async () => {
    try {
      setLoading(true);

      const personSubId = formRef.current?.getFieldValue(
        'person_subscription_id',
      );
      const categoryId = formRef.current?.getFieldValue('category_id');
      const partnerId = formRef.current?.getFieldValue('partner_id');

      if (!personSubId || !partnerId || !categoryId) {
        setPartnerServices([]);
        formRef.current?.setFieldValue('partner_service_id', null);
        return;
      }

      const response = await api.get<IPartnerService[]>(
        `/admin/partners/${partnerId}/services`,
        {
          params: { category_id: categoryId },
        },
      );
      setPartnerServices(response.data);

      const selected = response.data.find(
        (partnerService) =>
          partnerService.service_id === activeBonus?.service_id,
      );

      if (selected?.id) {
        setTimeout(() =>
          formRef.current?.setFieldValue('partner_service_id', selected?.id),
        );
      }
    } catch (error) {
      handleApiResponseErrors(
        error.response,
        'Erro ao buscar serviços da parceira.',
      );
    } finally {
      setLoading(false);
    }
  }, [activeBonus]);

  const handleOnSubmit: SubmitHandler = async (formData) => {
    try {
      setLoading(true);
      formRef.current?.setErrors({});

      const { success, data, errors } = await yupValidate(yupSchema, formData);

      if (!success) {
        return showFormErrors(errors, formRef);
      }

      if (params.id) {
        await api.put(`/admin/persons-bonuses/${params.id}`, data);
      } else {
        await api.post<IPersonBonusVoucher>('/admin/persons-bonuses', data);
      }

      toast.success('Dados salvos com sucesso!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro', yupSchema, formRef);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      if (status === 'active') {
        const isDelete = confirmDialogRef.current?.isDelete();
        let deleteConfig = {};
        if (isDelete) {
          deleteConfig = { data: { delete: true } };
        }

        await api.delete(`/admin/persons-bonuses/${params.id}`, deleteConfig);

        toast.success(`Bônus ${isDelete ? 'excluido' : 'desativado'}!`);
      } else {
        await api.post(`/admin/persons-bonuses/${params.id}/restore`);
        toast.success('Bônus restaurado!');
      }
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao atualizar bônus.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Form ref={formRef} onSubmit={handleOnSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <BackButton />

              <Typography variant="h5">
                {params.id ? 'Editar' : 'Novo'} Bônus para Assinante
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      name="person_id"
                      label="Pessoa"
                      options={persons.map((person) => ({
                        key: person.id,
                        label: person.name,
                        value: person.id,
                        disabled: person.status !== 'active',
                      }))}
                      onChange={handleChangePerson}
                      loading={layoutLoading}
                      textFieldProps={{ required: true }}
                      disabled={!!params.id}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      name="person_subscription_id"
                      label="Assinatura (validade)"
                      options={personSubscriptions.map((personSub) => ({
                        key: personSub.id,
                        label: `${personSub.id} - ${
                          personSub.subscription?.name
                        } ${
                          personSub.expires_at
                            ? `(${formatDate(
                                personSub.expires_at,
                                'dd/MM/yyyy',
                              )})`
                            : ''
                        }`,
                        value: personSub.id,
                        disabled: personSub.status === 'inactive',
                      }))}
                      onChange={handleChangePersonSubscription}
                      loading={loading}
                      textFieldProps={{ required: true }}
                      noOptionsText="Selecione uma Pessoa"
                      disabled={!!params.id}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      name="benefit_id"
                      label="Benefício"
                      options={benefits.map((benefit) => ({
                        key: benefit.id,
                        label: benefit.name,
                        value: benefit.id,
                        disabled: benefit.status !== 'active',
                      }))}
                      loading={layoutLoading}
                      onChange={handleBenefitChange}
                      textFieldProps={{ required: true }}
                      disabled={disableBenefit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      name="category_id"
                      label="Categoria"
                      noOptionsText="Selecione um Benefício"
                      options={categories.map((category) => ({
                        key: category.id,
                        label: category.name,
                        value: category.id,
                        disabled: category.status !== 'active',
                      }))}
                      loading={loading}
                      onChange={handleCategoryChange}
                      textFieldProps={{ required: true }}
                      disabled={disableBenefit}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      name="partner_id"
                      label="Parceira"
                      noOptionsText="Selecione uma Categoria"
                      options={partners.map((partner) => ({
                        key: partner.id,
                        label: partner.display_name,
                        value: partner.id,
                        disabled: partner.status !== 'active',
                      }))}
                      loading={loading}
                      onChange={handlePartnerChange}
                      textFieldProps={{ required: true }}
                      disabled={disableBenefit}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      name="partner_service_id"
                      label="Serviço da Parceira"
                      noOptionsText="Selecione uma Parceira"
                      options={partnerServices.map((partnerService) => ({
                        key: partnerService.id,
                        label: `${
                          partnerService.service?.name
                        } - ${masks.percent(
                          partnerService.discount.toString(),
                        )} %`,
                        value: partnerService.id,
                      }))}
                      loading={loading}
                      textFieldProps={{ required: true }}
                      disabled={disableBenefit}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      name="name"
                      label="Nome do Beneficiário"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="document"
                      label="CPF do Beneficiário"
                      required
                      mask="cpf"
                      returnUnmasked
                    />
                  </Grid>
                  {/* <Grid item xs={6} sm={4}>
                    <DatePicker
                      name="birth_date"
                      label="Nascimento"
                      required
                      clearable={false}
                      disableToolbar={false}
                    />
                  </Grid> */}
                  {params.id && (
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        name="created_at"
                        label="Data Emissão"
                        maxDate={new Date()}
                        valueAsDate
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container justify="space-between">
              {status === 'active' ? (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<CloseIcon />}
                  variant="contained"
                  color="secondary"
                  disabled={!params.id}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Desativar
                </Button>
              ) : (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<RestoreIcon />}
                  color="primary"
                  disabled={!params.id}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Restaurar
                </Button>
              )}

              <Button
                type="submit"
                loading={loading}
                startIcon={<CheckIcon />}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Form>

      <ConfirmDialog
        ref={confirmDialogRef}
        title={`${status === 'active' ? 'Desativar' : 'Restaurar'} bônus`}
        description="Confirma esta ação?"
        confirmColor={status === 'active' ? 'secondary' : 'primary'}
        onConfirm={handleConfirm}
        canDelete={status === 'active'}
      />
    </Container>
  );
};

export default Save;
